<script setup lang="ts">
import { ref } from 'vue'

const props = defineProps<{
  eventSlug: string
  eventName: string
}>()

const config = useRuntimeConfig()
const apiBase = config.public.apiBase as string

const form = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  type: 'Étudiant',
  subscribeNewsletter: false,
  website: '', // Honeypot
  formLoadedAt: 0
})

onMounted(() => {
  form.value.formLoadedAt = Date.now()
})

const isSubmitting = ref(false)
const isSuccess = ref(false)
const errorMessage = ref('')

const submitForm = async () => {
  isSubmitting.value = true
  errorMessage.value = ''
  
  try {
    await $fetch(`${apiBase}/oxoweb/events/${props.eventSlug}/register`, {
      method: 'POST',
      body: {
        firstName: form.value.firstName,
        lastName: form.value.lastName,
        email: form.value.email,
        phone: form.value.phone,
        type: form.value.type,
        subscribeNewsletter: form.value.subscribeNewsletter,
        website: form.value.website, // Honeypot
        formLoadedAt: form.value.formLoadedAt
      }
    })
    
    // Umami tracking
    if (typeof (window as any).umami !== 'undefined') {
      (window as any).umami.track('Event - Registration', { event: props.eventName })
    }
    
    isSuccess.value = true
  } catch (e: any) {
    console.error('Registration failed:', e)
    const errorData = e.response?._data
    errorMessage.value = errorData?.message || e.message || 'Une erreur est survenue. Veuillez réessayer.'
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <div class="bg-oxo-card-bg p-8 rounded-2xl shadow-lg border border-oxo-border transition-colors duration-300">
    <div v-if="!isSuccess">
      <h3 class="font-display font-bold text-2xl text-foreground mb-6">
        Inscription à l'événement
      </h3>
      
      <!-- Error Message -->
      <div v-if="errorMessage" class="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-sm">
        <i class="fas fa-exclamation-circle mr-2" aria-hidden="true"></i>
        {{ errorMessage }}
      </div>
      
      <form @submit.prevent="submitForm" class="space-y-4">
        <!-- Honeypot (hidden) -->
        <input type="text" v-model="form.website" class="hidden" tabindex="-1" autocomplete="off" />
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-bold text-foreground-dim mb-1">Prénom *</label>
            <input 
              v-model="form.firstName"
              type="text" 
              required
              class="w-full px-4 py-2 rounded-lg bg-background border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
              placeholder="John"
            />
          </div>
          <div>
            <label class="block text-sm font-bold text-foreground-dim mb-1">Nom *</label>
            <input 
              v-model="form.lastName"
              type="text" 
              required
              class="w-full px-4 py-2 rounded-lg bg-background border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
              placeholder="Doe"
            />
          </div>
        </div>

        <div>
          <label class="block text-sm font-bold text-foreground-dim mb-1">Email *</label>
          <input 
            v-model="form.email"
            type="email" 
            required
            class="w-full px-4 py-2 rounded-lg bg-background border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
            placeholder="john.doe@example.com"
          />
        </div>

        <div>
          <label class="block text-sm font-bold text-foreground-dim mb-1">Téléphone</label>
          <input 
            v-model="form.phone"
            type="tel" 
            class="w-full px-4 py-2 rounded-lg bg-background border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
            placeholder="06 12 34 56 78"
          />
        </div>

        <div>
          <label class="block text-sm font-bold text-foreground-dim mb-1">Vous êtes ?</label>
          <select 
            v-model="form.type"
            class="w-full px-4 py-2 rounded-lg bg-background border border-oxo-border focus:border-oxo-red focus:ring-1 focus:ring-oxo-red outline-hidden transition-colors text-foreground"
          >
            <option value="Étudiant">Étudiant / Lycéen</option>
            <option value="Parent">Parent</option>
            <option value="Pro">Professionnel</option>
            <option value="Visiteur">Autre</option>
          </select>
        </div>

        <!-- Newsletter Opt-in -->
        <div class="flex items-start gap-3 p-4 rounded-xl bg-background border border-oxo-border/30 hover:border-oxo-red/30 transition-all group">
          <div class="flex items-center h-5">
            <input 
              id="newsletter-event"
              v-model="form.subscribeNewsletter"
              type="checkbox" 
              class="w-5 h-5 rounded-sm border-oxo-border text-oxo-red focus:ring-oxo-red cursor-pointer transition-colors"
            />
          </div>
          <label for="newsletter-event" class="text-xs text-foreground-dim leading-relaxed cursor-pointer group-hover:text-foreground transition-colors font-medium">
            <i class="fas fa-envelope text-oxo-red mr-1" aria-hidden="true"></i>
            Je souhaite recevoir la newsletter OXO pour rester informé(e) des actualités, événements et opportunités.
          </label>
        </div>

        <div class="pt-4">
          <button 
            type="submit" 
            :disabled="isSubmitting"
            class="w-full py-3 px-6 rounded-lg bg-oxo-red text-white font-bold uppercase tracking-wider hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <i v-if="isSubmitting" class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
            <span v-else>Valider mon inscription</span>
          </button>
        </div>
        
        <p class="text-xs text-foreground-dim/60 text-center">
          En soumettant ce formulaire, vous acceptez d'être contacté par OXO School concernant cet événement.
        </p>
      </form>
    </div>

    <!-- Success State -->
    <div v-else class="text-center py-8">
      <div class="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
        <i class="fas fa-check" aria-hidden="true"></i>
      </div>
      <h3 class="font-display font-bold text-2xl text-foreground mb-2">
        Inscription confirmée !
      </h3>
      <p class="text-foreground-dim mb-6">
        Merci {{ form.firstName }}, nous avons bien reçu votre inscription pour <strong>{{ eventName }}</strong>.<br>
        Vous recevrez un email de confirmation dans quelques instants.
      </p>
      <button 
        @click="isSuccess = false; form.firstName = ''; form.lastName = ''; form.email = ''; form.phone = ''; form.subscribeNewsletter = false; form.formLoadedAt = Date.now()"
        class="text-oxo-red font-bold hover:underline"
      >
        Inscrire une autre personne
      </button>
    </div>
  </div>
</template>

